import React, { useState } from 'react';
import styled from 'styled-components';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { colors } from '../styles/GlobalStyles';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
`;

const Sidebar = styled.aside`
  width: ${props => props.collapsed ? '70px' : '250px'};
  background-color: ${colors.primary};
  color: white;
  transition: width 0.3s ease;
  position: fixed;
  height: 100vh;
  overflow-y: auto;
  z-index: 1000;
  left: 0;
  top: 0;

  @media (max-width: 768px) {
    width: 250px !important;
    left: ${props => props.mobileOpen ? '0' : '-250px'};
    transition: left 0.3s ease;
  }
`;

const SidebarHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const Logo = styled.h1`
  font-size: 20px;
  color: white;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Nav = styled.nav`
  padding: 20px 0;
`;

const NavItem = styled(NavLink)`
  display: flex;
  align-items: center;
  padding: 12px 20px;
  color: white;
  transition: all 0.3s ease;
  gap: 15px;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }

  &.active {
    background-color: rgba(255, 255, 255, 0.2);
    border-left: 4px solid white;
  }
`;

const NavIcon = styled.span`
  font-size: 20px;
  min-width: 24px;
`;

const NavText = styled.span`
  font-size: 16px;
  white-space: nowrap;
`;

const MainContent = styled.main.attrs(props => ({
  sidebarCollapsed: undefined,
}))`
  flex: 1;
  margin-left: ${props => props.sidebarCollapsed ? '70px' : '250px'};
  background-color: ${colors.background};
  transition: margin-left 0.3s ease;

  @media (max-width: 768px) {
    margin-left: 0;
  }
`;

const TopBar = styled.div`
  background-color: white;
  padding: 15px 30px;
  box-shadow: 0 2px 4px ${colors.shadow};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const MenuButton = styled.button`
  display: none;
  background: #f0f0f0;
  border: 2px solid ${colors.primary};
  font-size: 24px;
  color: ${colors.primary};
  cursor: pointer;
  padding: 12px;
  border-radius: 8px;
  transition: all 0.2s ease;
  position: relative;
  z-index: 997;
  outline: none;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;

  &:hover {
    background-color: #e0e0e0;
  }

  &:active {
    background-color: #d0d0d0;
    transform: scale(0.95);
  }

  &:focus {
    outline: 2px solid ${colors.primary};
    outline-offset: 2px;
  }

  @media (max-width: 768px) {
    display: block !important;
    min-width: 48px;
    min-height: 48px;
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`;

const LogoutButton = styled.button`
  background-color: ${colors.primary};
  color: white;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 0.9;
  }
`;

const Content = styled.div`
  padding: 30px;

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const MobileOverlay = styled.div`
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 998;

  @media (max-width: 768px) {
    display: ${props => props.$isOpen ? 'block' : 'none'};
  }
`;

export const MemberLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    toast.success('Çıkış yapıldı');
    navigate('/');
  };

  const handleMenuToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setMobileOpen(prev => !prev);
  };

  return (
    <Container>
      <MobileOverlay $isOpen={mobileOpen} onClick={() => setMobileOpen(false)} />
      <Sidebar collapsed={collapsed} mobileOpen={mobileOpen}>
        <SidebarHeader>
          {!collapsed && <Logo>Üye Paneli</Logo>}
        </SidebarHeader>
        <Nav>
          <NavItem to="/member/dashboard" onClick={() => setMobileOpen(false)}>
            <NavIcon>🏠</NavIcon>
            {!collapsed && <NavText>Ana Sayfa</NavText>}
          </NavItem>
          <NavItem to="/member/classes" onClick={() => setMobileOpen(false)}>
            <NavIcon>🧘</NavIcon>
            {!collapsed && <NavText>Ders Programı</NavText>}
          </NavItem>
          <NavItem to="/member/my-reservations" onClick={() => setMobileOpen(false)}>
            <NavIcon>📅</NavIcon>
            {!collapsed && <NavText>Rezervasyonlarım</NavText>}
          </NavItem>
          <NavItem to="/member/profile" onClick={() => setMobileOpen(false)}>
            <NavIcon>👤</NavIcon>
            {!collapsed && <NavText>Profilim</NavText>}
          </NavItem>
        </Nav>
      </Sidebar>

      <MainContent sidebarCollapsed={collapsed}>
        <TopBar>
          <MenuButton onClick={handleMenuToggle} aria-label="Menüyü Aç/Kapat">
            {mobileOpen ? '✕' : '☰'}
          </MenuButton>
          <UserInfo>
            <span>{user?.firstName} {user?.lastName}</span>
            <LogoutButton onClick={handleLogout}>Çıkış Yap</LogoutButton>
          </UserInfo>
        </TopBar>
        <Content>
          <Outlet />
        </Content>
      </MainContent>
    </Container>
  );
};

